/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import TargetMap from '../../actions/targetmap';
import { ACTION_CREATEWORK } from '../../store/state/actions';
import { isContainerSingleMode } from '../../container/container-utils';
import { getDirtyDetails } from '../../store/state/epics/util';
import APP_CONSTANTS from '../../constants';
import Utils from '../../utils/utils';
import EnvironmentInfo from '../../environment-info';
import { didOperatorHasAssignment, isAssignmentInCreateStage } from '../../utils/case-utils';
import type { Action } from '../../store/state/state-manager';
import type { DxAPIResponseObject } from '../types';
import { canLaunchCaseInModal } from '../common-helper';

export const STATE_PATHS = {
  NEXT_ASSIGNMENT_ID: 'data.nextAssignmentInfo.ID',
  FIRST_ASSIGNMENT_ID: 'data.caseInfo.assignments.0.ID',
  NEXT_ASSIGNMENT_INFO: 'nextAssignmentInfo',
  NEXT_ASSIGNMENT_INFO_CONTEXT: 'nextAssignmentInfo.context'
};

export const getContainerName = (createWorkActionPayload: Action['payload']) => {
  const { containerName, isInCreateStage } = createWorkActionPayload;
  if (isInCreateStage) return APP_CONSTANTS.MODAL;
  return containerName || TargetMap.getTargetViewContainerName(ACTION_CREATEWORK);
};

export const evaluateDirtyBeforeDxApiCall = (target: string | null) => {
  if (isContainerSingleMode(target)) {
    return {
      continueExecution: true,
      removeActionPayload: {}
    };
  }
  return getDirtyDetails(target);
};

export const isInCreateStage = (dxApiResponse: DxAPIResponseObject) => {
  const caseInfo = Utils.getIn(dxApiResponse, [APP_CONSTANTS.DATA, APP_CONSTANTS.CASE_INFO.CASE_INFO]);
  return (
    isAssignmentInCreateStage(caseInfo) && didOperatorHasAssignment(EnvironmentInfo.getOperatorIdentifier(), caseInfo)
  );
};

export const setDirectUrlUpdation = (
  dxApiResponse: DxAPIResponseObject,
  createWorkActionPayload: Action['payload']
) => {
  const isInCreateStageRes = isInCreateStage(dxApiResponse);
  const { skipBrowserSemanticUrlUpdate = false, isTraditionalUICase } = createWorkActionPayload;
  const canUpdateTraditionalCaseURL = isTraditionalUICase && !canLaunchCaseInModal();
  // URL and title updation should be done only for non create stages.
  Utils.setIn(
    dxApiResponse,
    ['directUrlUpdation'],
    (!isInCreateStageRes || canUpdateTraditionalCaseURL) && !skipBrowserSemanticUrlUpdate
  );
};

export const getCaseViewMode = (dxApiResponse: DxAPIResponseObject) => {
  if (dxApiResponse.confirmationNote) {
    return APP_CONSTANTS.REVIEW_MODE;
  }
  return APP_CONSTANTS.PERFORM_MODE;
};
